import React, { useEffect, useState } from 'react';
import { documentDownloadService } from '../../services/documentDownloadService';
import type { SignaturePlacementInputDto } from '../../services/documentSignatureService';
import { PdfSignaturePlacementEditor } from './PdfSignaturePlacementEditor';

export interface HtmlSignaturePlacementEditorProps {
  documentId?: string;
  htmlContent?: string | null;
  signerName: string;
  placement?: SignaturePlacementInputDto | null;
  onPlacementChange: (placement: SignaturePlacementInputDto | null) => void;
}

export const HtmlSignaturePlacementEditor: React.FC<HtmlSignaturePlacementEditorProps> = ({
  documentId,
  htmlContent,
  signerName,
  placement,
  onPlacementChange,
}) => {
  const [loading, setLoading] = useState(!!documentId);
  const [error, setError] = useState('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadPreview = async () => {
      if (!documentId)
      {
        setLoading(false);
        setError('');
        setPreviewBlobUrl(null);
        return;
      }

      setLoading(true);
      setError('');
      setPreviewBlobUrl(null);

      try
      {
        const blob = await documentDownloadService.previewPdf(documentId);
        if (cancelled)
        {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        if (cancelled)
        {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }

        setPreviewBlobUrl(objectUrl);
      }
      catch (err)
      {
        if (!cancelled)
        {
          setError(
            err instanceof Error
              ? err.message
              : 'Não foi possível gerar a prévia PDF para posicionamento.'
          );
        }
      }
      finally
      {
        if (!cancelled)
        {
          setLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl)
      {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [documentId]);

  if (!documentId)
  {
    const hasHtml = !!htmlContent && htmlContent.trim().length > 0;
    return (
      <div className="space-y-2">
        <p className="text-sm text-amber-700">
          {hasHtml
            ? 'O posicionamento visual sobre a prévia PDF só está disponível após o documento eletrônico ser salvo. Use a posição padrão (final do documento) ou salve o documento e solicite a assinatura em seguida.'
            : 'Documento eletrônico ainda não disponível para posicionamento. Use a posição padrão (final do documento).'}
        </p>
        <button
          type="button"
          className="text-xs text-indigo-600 hover:underline"
          onClick={() => onPlacementChange(null)}
        >
          Usar posição padrão (final do documento)
        </button>
      </div>
    );
  }

  if (loading)
  {
    return (
      <div
        className="flex min-h-[280px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-6 py-10"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
        <p className="text-center text-base font-medium text-gray-900">
          Gerando prévia PDF para posicionamento
        </p>
        <p className="mt-2 max-w-sm text-center text-sm text-gray-500">
          Aguarde alguns segundos. Em seguida você poderá clicar na página para posicionar a
          assinatura.
        </p>
      </div>
    );
  }

  if (error)
  {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-600">{error}</p>
        <button
          type="button"
          className="text-xs text-indigo-600 hover:underline"
          onClick={() => onPlacementChange(null)}
        >
          Usar posição padrão (final do documento)
        </button>
      </div>
    );
  }

  if (!previewBlobUrl)
  {
    return (
      <p className="text-sm text-red-600">
        Prévia PDF não disponível para posicionamento.
      </p>
    );
  }

  return (
    <PdfSignaturePlacementEditor
      previewBlobUrl={previewBlobUrl}
      signerName={signerName}
      placement={placement}
      onPlacementChange={onPlacementChange}
    />
  );
};
